import React from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import classnames from 'classnames/bind';

import styles from './StackedHorizontalBarChart.module.css';

const cx = classnames.bind(styles);

/**
 * **Access:**
 * ```js
 * import {StackedHorizontalBarChart} from '@iss/client-react-components'
 * ```
 * Provides a stacked horizontal  bar chart.
 *
 * @class StackedHorizontalBarChart
 * @component
 */
export default class StackedHorizontalBarChart extends React.Component {
  static propTypes = {
    legendLabels: PropTypes.arrayOf(PropTypes.string),
    rows: PropTypes.arrayOf(PropTypes.object),
    columnToColorMap: PropTypes.func,
    maximumX: PropTypes.number,
  };

  ref = React.createRef();

  get svg() {
    return d3.select(this.ref.current);
  }

  get rect() {
    return this.ref.current.getBoundingClientRect();
  }

  componentDidMount() {
    this.draw();
  }

  componentDidUpdate() {
    this.draw();
  }

  draw() {
    const { legendLabels, rowLabels, rows, xAxisTitle } = this.props;
    const series = d3.stack().keys(legendLabels).order(d3.stackOrderNone).offset(d3.stackOffsetNone)(rows);

    const svg = this.svg;
    const { width } = this.rect;
    const margin = { top: 10, right: 30, bottom: 15, left: 30 };

    const barHeight = 20;
    const barSeparation = 36;
    const barInterval = barHeight + barSeparation;
    const height = barInterval * rowLabels.length;
    const yScale = d3
      .scaleBand()
      .domain(rowLabels)
      .rangeRound([margin.top, margin.top + height])
      .padding(barSeparation / barInterval);

    const maxDomainValue =
      this.props.maximumX || d3.max(series, (rowElements) => d3.max(rowElements, (d) => d3.max(d)));
    const xScale = d3
      .scaleLinear()
      .domain([0, maxDomainValue])
      .rangeRound([margin.left, width - margin.right]);

    // axes
    const xAxis = d3
      .axisBottom(xScale)
      .ticks(maxDomainValue < 4 ? maxDomainValue : 5, 'f')
      .tickSize(-height + (margin.top + margin.bottom));

    // tick marks and vertical lines
    svg
      .append('g')
      .attr('transform', 'translate(0, ' + (height - margin.bottom) + ')')
      .call((g) => {
        g.call(xAxis);
        g.select('.domain').remove();
        g.selectAll('.tick line').attr('stroke', '#DDDDDD');
      });

    // Title for the X-axis
    svg
      .append('text')
      .attr('transform', 'translate(' + width / 2 + ' ,' + (height + 25) + ')')
      .attr('class', cx('x-axis-title'))
      .text(xAxisTitle);

    // row labels (factor names)
    const pushLabelUpPx = 5;
    const pushLabelRightPx = 2;
    svg
      .selectAll(null)
      .data(rowLabels)
      .enter()
      // .append('rowLabel')
      .append('text')
      .attr('class', cx('label'))
      .attr('x', margin.left + pushLabelRightPx)
      .attr('y', (d, i) => yScale(rowLabels[i]) - pushLabelUpPx)
      .text((d, i) => rowLabels[i]);

    // filled rectangles
    svg
      .selectAll(null)
      .data(series)
      .enter()
      .append('g')
      .attr('fill', (d) => this.props.columnToColorMap(d.key))

      .selectAll(null)
      .data((d) => d)
      .enter()
      .append('rect')
      .attr('x', (d) => xScale(d[0]))
      .attr('y', (d, i) => yScale(rowLabels[i]))
      .attr('width', (d) => xScale(d[1]) - xScale(d[0]))
      .attr('height', yScale.bandwidth());
  }

  render() {
    const { title, columnToColorMap, legendLabels } = this.props;

    return (
      <div className={cx('root')}>
        <h4 className={cx('title')}>{title} </h4>
        <div className={cx('box')}>
          <svg width="380" height="430" ref={this.ref} />
          <div className={cx('legend')}>
            {Array.from(legendLabels).map((legendLabel) => (
              <Dot key={legendLabel} color={columnToColorMap(legendLabel)}>
                {legendLabel}
              </Dot>
            ))}
          </div>
        </div>
      </div>
    );
  }
}

function Dot({ color, children }) {
  return (
    <span className={cx('legend-entry')}>
      <svg viewBox="0 0 15 15" width="15" height="15">
        <circle fill={color} cx="7.5" cy="7.5" r="7.5" />
      </svg>
      <span className={cx('legend-label')}>{children} </span>
    </span>
  );
}
